import React from 'react';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputTextarea } from 'primereact/inputtextarea';
import { Button } from 'primereact/button';
import SellCustomerDropdown from './SellCustomerDropdown';
import CustomerDefinitionDialog from '../../../warranty-management/customer-definition/components/CustomerDefinitionDialog';

const SellToCustomerForm = ({
    th,
    customerId,
    customerData,
    stockId,
    price,
    description,
    errors,
    stockOptions,
    selectedStock,
    isStockLoading,
    isSelling,
    handleCustomerChange,
    handleStockChange,
    handlePriceChange,
    handleDescriptionChange,
    handleSave,
    resetForm,
    customerDefVisible,
    customerFormData,
    setCustomerFormData,
    customerErrors,
    newCustomer,
    isCreatingCustomer,
    addChassis,
    removeChassis,
    handleOpenCustomerDefinition,
    handleCloseCustomerDefinition,
    handleSaveNewCustomer
}) => {
    const detailRow = (label, value) => (
        <div className="flex justify-content-between gap-2 py-1">
            <span className="text-600 text-sm">{label}</span>
            <span className="text-sm font-medium text-right">{value || '-'}</span>
        </div>
    );

    return (
        <>
        <div className="grid">
            <div className="col-12 md:col-6 lg:col-4">
                <div className="field">
                    <label className="block mb-2 text-sm font-medium">
                        {th('vehicleSalesSellToCustomer.fields.customer')}
                    </label>
                    <div className="flex gap-2">
                        <div className="flex-1">
                            <SellCustomerDropdown
                                value={customerId}
                                onChange={handleCustomerChange}
                                newCustomer={newCustomer}
                                className={`w-full ${errors.customer ? 'p-invalid' : ''}`}
                                placeholder={th('vehicleSalesSellToCustomer.fields.customerPlaceholder')}
                            />
                        </div>
                        <Button
                            icon="pi pi-plus"
                            className="p-button-outlined"
                            onClick={handleOpenCustomerDefinition}
                            tooltip={th('customerDefinition.newTitle')}
                            type="button"
                            style={{ minWidth: '2.5rem' }}
                        />
                    </div>
                    {errors.customer && <small className="p-error block mt-1">{errors.customer}</small>}
                </div>
            </div>

            <div className="col-12 md:col-6 lg:col-4">
                <div className="field">
                    <label className="block mb-2 text-sm font-medium">
                        {th('vehicleSalesSellToCustomer.fields.chassis')}
                    </label>
                    <Dropdown
                        value={stockId}
                        options={stockOptions}
                        optionLabel="label"
                        optionValue="value"
                        onChange={(e) => handleStockChange(e.value)}
                        className={`w-full ${errors.stock ? 'p-invalid' : ''}`}
                        placeholder={th('vehicleSalesSellToCustomer.fields.chassisPlaceholder')}
                        loading={isStockLoading}
                        filter
                        showClear
                        emptyMessage={th('common.noRecords')}
                    />
                    {errors.stock && <small className="p-error block mt-1">{errors.stock}</small>}
                </div>
            </div>

            <div className="col-12 md:col-6 lg:col-4">
                <div className="field">
                    <label className="block mb-2 text-sm font-medium">
                        {th('vehicleSalesSellToCustomer.fields.price')}
                    </label>
                    <InputNumber
                        value={price}
                        onValueChange={(e) => handlePriceChange(e.value)}
                        mode="decimal"
                        minFractionDigits={2}
                        maxFractionDigits={2}
                        min={0}
                        className={`w-full ${errors.price ? 'p-invalid' : ''}`}
                        placeholder={th('vehicleSalesSellToCustomer.fields.pricePlaceholder')}
                    />
                    {errors.price && <small className="p-error block mt-1">{errors.price}</small>}
                </div>
            </div>

            <div className="col-12">
                <div className="field">
                    <label className="block mb-2 text-sm font-medium">
                        {th('vehicleSalesSellToCustomer.fields.description')}
                    </label>
                    <InputTextarea
                        value={description}
                        onChange={(e) => handleDescriptionChange(e.target.value)}
                        rows={3}
                        className="w-full"
                        maxLength={500}
                        placeholder={th('vehicleSalesSellToCustomer.fields.descriptionPlaceholder')}
                    />
                </div>
            </div>

            {customerData && (
                <div className="col-12 md:col-6">
                    <div className="surface-100 border-round p-3 h-full">
                        <div className="text-sm font-semibold mb-2">
                            {th('vehicleSalesSellToCustomer.customer.title')}
                        </div>
                        {detailRow(th('vehicleSalesSellToCustomer.customer.name'), customerData.name)}
                        {detailRow(th('vehicleSalesSellToCustomer.customer.surname'), customerData.surname)}
                        {detailRow(th('vehicleSalesSellToCustomer.customer.identityNo'), customerData.identityNo)}
                        {detailRow(th('vehicleSalesSellToCustomer.customer.phoneNumber'), customerData.phoneNumber)}
                        {detailRow(th('vehicleSalesSellToCustomer.customer.address'), customerData.address)}
                        {detailRow(th('vehicleSalesSellToCustomer.customer.country'), customerData.country)}
                        {detailRow(th('vehicleSalesSellToCustomer.customer.city'), customerData.city)}
                    </div>
                </div>
            )}

            {selectedStock && (
                <div className="col-12 md:col-6">
                    <div className="surface-100 border-round p-3 h-full">
                        <div className="text-sm font-semibold mb-2">
                            {th('vehicleSalesSellToCustomer.vehicle.title')}
                        </div>
                        {detailRow(th('vehicleSalesSellToCustomer.vehicle.chassisNo'), (selectedStock.chassisNo || '').trim())}
                        {detailRow(th('vehicleSalesSellToCustomer.vehicle.brand'), selectedStock.brand)}
                        {detailRow(th('vehicleSalesSellToCustomer.vehicle.model'), selectedStock.model)}
                        {detailRow(th('vehicleSalesSellToCustomer.vehicle.matnr'), selectedStock.matnr)}
                        {detailRow(th('vehicleSalesSellToCustomer.vehicle.maktx'), selectedStock.maktx)}
                        {detailRow(th('vehicleSalesSellToCustomer.vehicle.status'), selectedStock.statusText)}
                        {detailRow(th('vehicleSalesSellToCustomer.vehicle.sellable'), selectedStock.sellableText)}
                    </div>
                </div>
            )}

            <div className="col-12 flex justify-content-end gap-2 mt-2">
                <Button
                    label={th('vehicleSalesSellToCustomer.buttons.cancel')}
                    icon="pi pi-times"
                    className="p-button-outlined"
                    onClick={resetForm}
                    disabled={isSelling}
                />
                <Button
                    label={th('vehicleSalesSellToCustomer.buttons.save')}
                    icon="pi pi-check"
                    onClick={handleSave}
                    loading={isSelling}
                />
            </div>
        </div>

        <CustomerDefinitionDialog
            visible={customerDefVisible}
            onHide={handleCloseCustomerDefinition}
            formData={customerFormData}
            setFormData={setCustomerFormData}
            errors={customerErrors}
            handleSave={handleSaveNewCustomer}
            editMode={false}
            isSubmitting={isCreatingCustomer}
            addChassis={addChassis}
            removeChassis={removeChassis}
        />
        </>
    );
};

export default SellToCustomerForm;
